import {
  getCanonicalThreadPath,
  getOrgSettingsPurchasePath,
} from '@/lib/thread-routes';
import { yourImaiHref } from '@/lib/your-imai-routes';

export type PersistentVoiceCallSource = {
  orgThreadId: string;
  organizationId: string;
  orgSlug: string;
  routeRef: string;
  title: string;
  sourceKind?: 'your-imai';
  positionKey?: string;
};

export function getPersistentVoiceCallHref(
  source: PersistentVoiceCallSource | PendingPersistentVoiceCallSource
): string {
  return source.sourceKind === 'your-imai'
    ? yourImaiHref(source.orgSlug)
    : getCanonicalThreadPath(source.orgSlug, source.routeRef);
}

export type PendingPersistentVoiceCallSource = Omit<
  PersistentVoiceCallSource,
  'orgThreadId'
> & { orgThreadId?: undefined };

export type PersistentVoiceCall = (
  | PersistentVoiceCallSource
  | PendingPersistentVoiceCallSource
) & {
  sessionId: string;
  isEnding?: boolean;
};

export function beginPersistentVoiceCall(
  current: PersistentVoiceCall | null,
  source: PersistentVoiceCallSource | PendingPersistentVoiceCallSource,
  sessionId: string
): {
  call: PersistentVoiceCall;
  outcome: 'started' | 'already-active' | 'different-thread-active';
} {
  if (current) {
    if (!current.orgThreadId) {
      const sameSource =
        current.orgSlug === source.orgSlug &&
        current.organizationId === source.organizationId &&
        current.routeRef === source.routeRef;
      if (sameSource && source.orgThreadId) {
        return {
          call: { ...source, sessionId: current.sessionId },
          outcome: 'started',
        };
      }
      return {
        call: current,
        outcome: sameSource ? 'already-active' : 'different-thread-active',
      };
    }
    return {
      call: current,
      outcome:
        current.orgThreadId === source.orgThreadId
          ? 'already-active'
          : 'different-thread-active',
    };
  }
  return { call: { ...source, sessionId }, outcome: 'started' };
}

export function shouldEndPersistentVoiceCallForPath(
  pathname: string,
  orgSlug: string
): boolean {
  const segments =
    pathname.split(/[?#]/, 1)[0]?.split('/').filter(Boolean) ?? [];
  const routeOrgSlug = segments[0];
  if (routeOrgSlug === 'shared' && segments.length === 1) return false;
  return (
    routeOrgSlug !== encodeURIComponent(orgSlug) && routeOrgSlug !== 'settings'
  );
}

/**
 * A call opened without enough credits shows the low-credit message in the
 * call UI; the dock offers the top-up next to it instead of leaving the page.
 */
export function getVoiceCallTopUpHref({
  orgSlug,
  errorText,
  lowCreditMessage,
}: {
  orgSlug: string;
  errorText: string | null | undefined;
  lowCreditMessage: string;
}): string | null {
  return errorText && errorText === lowCreditMessage
    ? getOrgSettingsPurchasePath(orgSlug)
    : null;
}
